import { Flex, Text } from '@chakra-ui/react'
import { canadianProvinces, usStates } from '@sequencehq/api'
import {
  InputSelectControl,
  SettingsFormControl,
  TextInputField
} from '@sequencehq/core-components'
import { countries } from '@sequencehq/core-models'
import { GreyGrey70, Lato13Bold } from '@sequencehq/design-tokens'

import { FC, ReactNode } from 'react'
import { Merchant, useMerchantSettings } from './useMerchantSettings'

type MerchantAddressFormControlProps = {
  label: string
  htmlFor: string
  children: ReactNode
}

const MerchantAddressFormControl: FC<MerchantAddressFormControlProps> = ({
  label,
  htmlFor,
  children
}) => {
  return (
    <Flex height="32px" alignItems="center" justifyContent="space-between">
      <Flex flexDirection="column" height="fit-content" gap="2px">
        <Text as="label" htmlFor={htmlFor} color={GreyGrey70} {...Lato13Bold}>
          {label}
        </Text>
      </Flex>

      {children}
    </Flex>
  )
}

export const MerchantAddressSettings = (props: { merchant: Merchant }) => {
  const { fields, getInputFieldProps, getSelectFieldProps } =
    useMerchantSettings(props.merchant)
  const postcodeLabel = fields.country.value === 'US' ? 'Zip' : 'Postal code'

  return (
    <>
      <SettingsFormControl label="Country" htmlFor="company-country">
        <InputSelectControl
          options={Object.entries(countries).map(([key, label]) => ({
            value: key,
            label: label
          }))}
          {...getSelectFieldProps('country', {
            id: 'company-country',
            placeholder: 'Select country',
            label: 'State'
          })}
        />
      </SettingsFormControl>

      {fields.country.value && (
        <Flex flexDirection="column" gap="16px" paddingY="16px">
          <MerchantAddressFormControl
            label="Address line 1"
            htmlFor="address-line-1"
          >
            <TextInputField
              {...getInputFieldProps('line1', {
                id: 'address-line-1',
                placeholder: 'Address line 1',
                label: 'Line 1'
              })}
            />
          </MerchantAddressFormControl>

          <MerchantAddressFormControl
            label="Address line 2 (optional)"
            htmlFor="address-line-2"
          >
            <TextInputField
              {...getInputFieldProps('line2', {
                id: 'address-line-2',
                placeholder: 'Address line 2',
                label: 'Line 2'
              })}
            />
          </MerchantAddressFormControl>

          <MerchantAddressFormControl label="City" htmlFor="city">
            <TextInputField
              {...getInputFieldProps('town', {
                id: 'city',
                placeholder: 'City',
                label: 'City'
              })}
            />
          </MerchantAddressFormControl>

          {['US', 'CA'].includes(fields.country.value) && (
            <MerchantAddressFormControl label="State" htmlFor="state">
              <InputSelectControl
                options={Object.entries(
                  fields.country.value === 'US' ? usStates : canadianProvinces
                ).map(([key, label]) => ({
                  value: key,
                  label: label
                }))}
                {...getSelectFieldProps('state', {
                  id: 'state',
                  label: 'State',
                  placeholder: 'Select state'
                })}
              />
            </MerchantAddressFormControl>
          )}

          <MerchantAddressFormControl
            label={postcodeLabel}
            htmlFor="postal-code"
          >
            <TextInputField
              {...getInputFieldProps('postcode', {
                id: 'postal-code',
                placeholder: postcodeLabel,
                label: postcodeLabel
              })}
            />
          </MerchantAddressFormControl>
        </Flex>
      )}
    </>
  )
}
